import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  getDocs, 
  addDoc, 
  setDoc,
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
  disableNetwork,
  enableNetwork,
  DocumentData
} from 'firebase/firestore';
import { LatLngExpression } from 'leaflet';

// Define Journey interface to support both string format and object format coordinates
interface Journey {
  id: string;
  title: string;
  date: string;
  content: string;
  coordinates: string | {
    longitude: number;
    latitude: number;
  };
  zoom: string | number;
  image_url: string;
  created_at?: string | Date;
  updated_at?: string | Date;
}

// Firebase configuration with the correct API key
const firebaseConfig = {
  apiKey: "AIzaSyDTOsng7iPTk30W-P8v3J_D81p32-jpU3g",
  authDomain: "xquest-f7c63.firebaseapp.com",
  projectId: "xquest-f7c63",
  storageBucket: "xquest-f7c63.appspot.com",
  messagingSenderId: "1086248009209",
  appId: "1:1086248009209:web:a612e77ddec2a6eb9e83c3"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Disable persistent connections to avoid WebSocket errors
disableNetwork(db).then(() => {
  console.log("Persistent connections disabled");
}).catch((err: Error) => {
  console.error("Error disabling network:", err);
});

// Supabase Journey Data
const supabaseJourneys: Journey[] = [
  {
    "id": "a12e8dc8-bfea-4e79-a615-b00459a03c80",
    "title": "Konohana Ward, Osaka",
    "date": "1938",
    "content": "A bustling commercial district in pre-war Osaka, filled with the energy of street vendors and modern shops.",
    "coordinates": "(135.4667,34.6833)",
    "zoom": 15,
    "image_url": "/images/osaka-night.jpg",
    "created_at": "2025-04-17 04:28:38.094568+00",
    "updated_at": "2025-04-17 04:28:38.094568+00"
  },
  {
    "id": "163b52ca-191d-49b7-a939-a856b4f12580",
    "title": "Mia-dong, Seoul",
    "date": "1942",
    "content": "The family lived in a very modest home upon returning to Seoul from Osaka.",
    "coordinates": "(127.0244,37.6199)",
    "zoom": 16,
    "image_url": "/images/miadong-home.jpg",
    "created_at": "2025-04-17 04:28:38.094568+00",
    "updated_at": "2025-04-17 04:28:38.094568+00"
  },
  {
    "id": "ac505f18-9fa5-4c67-b763-71513a15cd22",
    "title": "Sookmyung Girls' School",
    "date": "1944",
    "content": "One of the most prestigious girls' schools in post-liberation Seoul, representing hope and education in a new era.",
    "coordinates": "(127.0533366,37.4907289)",
    "zoom": 16,
    "image_url": "/images/sookmyung-students.jpg",
    "created_at": "2025-04-17 04:28:38.094568+00",
    "updated_at": "2025-04-17 04:28:38.094568+00"
  },
  {
    "id": "ece15e1e-f272-441a-8d93-8200bc181c0d",
    "title": "Hirata Department Store",
    "date": "1947",
    "content": "A landmark store in Seoul's Chungmuro district, tragically destroyed in a devastating fire.",
    "coordinates": "(127.002222,37.566111)",
    "zoom": 16,
    "image_url": "/images/hirata-store.jpg",
    "created_at": "2025-04-17 04:28:38.094568+00",
    "updated_at": "2025-04-17 04:28:38.094568+00"
  },
  {
    "id": "a6df8c0a-c113-427c-b573-a91e5e3c4cb6",
    "title": "Busan Seoul National University",
    "date": "1951",
    "content": "The temporary wartime campus of Seoul National University, where education continued despite the challenges of war.",
    "coordinates": "(129.1027,35.1378)",
    "zoom": 15,
    "image_url": "/images/busan-students.jpg",
    "created_at": "2025-04-17 04:28:38.094568+00",
    "updated_at": "2025-04-17 04:28:38.094568+00"
  },
  {
    "id": "85cecf90-a4f2-4c63-95ac-c9d347eb1e8e",
    "title": "Seoul National University",
    "date": "1954",
    "content": "The restored main campus in Seoul, symbolizing the return to normalcy and the pursuit of knowledge in the post-war period.",
    "coordinates": "(126.997778,37.5875)",
    "zoom": 15,
    "image_url": "/images/seoul-university.jpg",
    "created_at": "2025-04-17 04:28:38.094568+00",
    "updated_at": "2025-04-17 04:28:38.094568+00"
  },
  {
    "id": "9632dc25-6879-456c-b975-24d0822cd469",
    "title": "Gimpo Airport",
    "date": "1959",
    "content": "Seoul's primary international gateway, connecting South Korea to the world during a period of rapid modernization.",
    "coordinates": "(126.79056,37.55806)",
    "zoom": 14,
    "image_url": "/images/gimpo-airport.jpg",
    "created_at": "2025-04-17 04:28:38.094568+00",
    "updated_at": "2025-04-17 04:28:38.094568+00"
  }
];

// Map Component for journeys
const MapView = ({ journeys }: { journeys: Journey[] }) => {
  useEffect(() => {
    // Skip if no journeys or if leaflet is not loaded
    if (!journeys || journeys.length === 0 || !window.L) return;

    // Get coordinates from the first journey
    const firstJourney = journeys[0];
    // Parse coordinates from string format or object format
    let firstCoordinates: [number, number] = [0, 0];
    
    if (typeof firstJourney.coordinates === 'string' && firstJourney.coordinates.includes(',')) {
      // Parse from "(lng,lat)" format
      const coordsStr = firstJourney.coordinates.replace('(', '').replace(')', '');
      const [lng, lat] = coordsStr.split(',').map((coord: string) => parseFloat(coord.trim()));
      firstCoordinates = [lat, lng]; // Swap because map needs [lat, lng]
    } else if (firstJourney.coordinates && typeof firstJourney.coordinates === 'object') {
      // Handle object format from import_journeys.ts
      const { latitude, longitude } = firstJourney.coordinates;
      firstCoordinates = [latitude, longitude];
    }

    // Create a map instance centered on the first journey
    // Convert zoom from string to number if needed
    const zoomLevel = typeof firstJourney.zoom === 'string' ? parseInt(firstJourney.zoom) || 13 : firstJourney.zoom;
    const map = window.L.map('map').setView(firstCoordinates as LatLngExpression, zoomLevel);

    // Add OpenStreetMap tiles
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    // Add markers for each journey
    journeys.forEach(journey => {
      let markerCoordinates: [number, number] = [0, 0];
      
      // Parse coordinates from string format or object format
      if (typeof journey.coordinates === 'string' && journey.coordinates.includes(',')) {
        // Parse from "(lng,lat)" format
        const coordsStr = journey.coordinates.replace('(', '').replace(')', '');
        const [lng, lat] = coordsStr.split(',').map((coord: string) => parseFloat(coord.trim()));
        markerCoordinates = [lat, lng]; // Swap because map needs [lat, lng]
      } else if (journey.coordinates && typeof journey.coordinates === 'object') {
        // Handle object format from import_journeys.ts
        const { latitude, longitude } = journey.coordinates;
        markerCoordinates = [latitude, longitude];
      }

      // Create an image URL for the popup
      let imageElement = '';
      if (journey.image_url) {
        // Check if it's a relative path or full URL
        if (journey.image_url.startsWith('http')) {
          imageElement = `<img src="${journey.image_url}" alt="${journey.title}" style="max-width: 200px; margin-top: 8px;">`;
        } else {
          // For relative paths, we'll use a placeholder for now
          imageElement = `<p style="color: #666; font-style: italic;">Image: ${journey.image_url}</p>`;
        }
      }

      // Create popup content with title, date, and content
      const popupContent = `
        <div>
          <h3 style="margin: 0; font-weight: bold;">${journey.title || 'Untitled'}</h3>
          <p style="margin: 4px 0; color: #666;">${journey.date || ''}</p>
          <p style="margin: 4px 0;">${journey.content || ''}</p>
          ${imageElement}
        </div>
      `;
      
      window.L.marker(markerCoordinates as LatLngExpression)
        .addTo(map)
        .bindPopup(popupContent);
    });

    // Fit the map to show all markers if there are multiple points
    if (journeys.length > 1) {
      const bounds = journeys.map(journey => {
        let coords: [number, number] = [0, 0];
        
        if (typeof journey.coordinates === 'string' && journey.coordinates.includes(',')) {
          const coordsStr = journey.coordinates.replace('(', '').replace(')', '');
          const [lng, lat] = coordsStr.split(',').map((coord: string) => parseFloat(coord.trim()));
          coords = [lat, lng]; // Swap because map needs [lat, lng]
        } else if (journey.coordinates && typeof journey.coordinates === 'object') {
          const { latitude, longitude } = journey.coordinates;
          coords = [latitude, longitude];
        }
        
        return coords;
      });
      
      map.fitBounds(bounds as LatLngExpression[]);
    }

    // Cleanup on component unmount
    return () => {
      map.remove();
    };
  }, [journeys]);

  return (
    <div id="map" style={{ height: '400px', width: '100%', borderRadius: '0.375rem' }}></div>
  );
};

function App() {
  // Authentication states
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [user, setUser] = useState<User | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Data states
  const [docData, setDocData] = useState<Journey | null>(null);
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedJourney, setSelectedJourney] = useState<Journey | null>(null);
  const [showingMap, setShowingMap] = useState<boolean>(false);
  const [importSuccess, setImportSuccess] = useState<boolean>(false);

  // Ensure Leaflet is loaded
  useEffect(() => {
    // Add Leaflet CSS
    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    linkElement.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    linkElement.crossOrigin = '';
    document.head.appendChild(linkElement);

    // Add Leaflet JS
    const scriptElement = document.createElement('script');
    scriptElement.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    scriptElement.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    scriptElement.crossOrigin = '';
    document.body.appendChild(scriptElement);

    return () => {
      document.head.removeChild(linkElement);
      document.body.removeChild(scriptElement);
    };
  }, []);

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        setUser(user);
        console.log("User authenticated:", user.uid);
      } else {
        // User is signed out
        setUser(null);
        console.log("No user signed in");
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Handle sign up
  const handleSignUp = async () => {
    setLoading(true);
    setAuthError(null);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log("User created:", userCredential.user);
    } catch (error) {
      console.error("Error creating user:", error);
      setAuthError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle sign in
  const handleSignIn = async () => {
    setLoading(true);
    setAuthError(null);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("User signed in:", userCredential.user);
    } catch (error) {
      console.error("Error signing in:", error);
      setAuthError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      console.log("User signed out");
      // Clear any fetched data
      setDocData(null);
      setJourneys([]);
      setSelectedJourney(null);
      setShowingMap(false);
      setImportSuccess(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Fetch a specific journey
  const fetchJourney = async () => {
    if (!user) {
      setFetchError("You must be signed in to fetch data");
      return;
    }

    setLoading(true);
    setFetchError(null);

    try {
      // Enable network temporarily for this operation
      await enableNetwork(db);
      
      console.log("Attempting to fetch journey with ID: a12e8dc8-bfea-4e79-a615-b00459a03c80");
      const docRef = doc(db, "journeys", "a12e8dc8-bfea-4e79-a615-b00459a03c80");
      console.log("Document reference created");
      
      const docSnap = await getDoc(docRef);
      console.log("Document snapshot received");
      
      if (docSnap.exists()) {
        const data = {
          id: docSnap.id,
          ...docSnap.data()
        } as Journey;
        console.log("Journey exists with data:", data);
        setDocData(data);
        setJourneys([data]); // Add to journeys array for map view
      } else {
        console.log("No such journey!");
        setFetchError("Journey not found - please import journeys first");
      }
      
      // Disable network again after operation completes
      await disableNetwork(db);
    } catch (error) {
      console.error("Error fetching journey:", error);
      setFetchError(`Error fetching journey: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all journeys
  const fetchJourneys = async () => {
    if (!user) {
      setFetchError("You must be signed in to fetch data");
      return;
    }

    setLoading(true);
    setFetchError(null);

    try {
      // Enable network temporarily for this operation
      await enableNetwork(db);
      
      console.log("Fetching journeys...");
      const journeysRef = collection(db, "journeys");
      console.log("Collection reference created");
      
      const snapshot = await getDocs(journeysRef);
      console.log("Snapshot received, docs count:", snapshot.docs.length);
      
      if (snapshot.empty) {
        console.log("No journeys found");
        setFetchError("No journeys found - please import journeys first");
        setJourneys([]);
      } else {
        const journeysList = snapshot.docs.map(doc => {
          console.log("Processing document ID:", doc.id);
          const data = doc.data();
          console.log("Document data:", data);
          return {
            id: doc.id,
            ...data
          } as Journey;
        });
        
        // Sort journeys by date
        journeysList.sort((a, b) => {
          const dateA = parseInt(a.date) || 0;
          const dateB = parseInt(b.date) || 0;
          return dateA - dateB;
        });
        
        console.log("Processed journeys list:", journeysList);
        setJourneys(journeysList);
      }
      
      // Disable network again after operation completes
      await disableNetwork(db);
    } catch (error) {
      console.error("Error fetching journeys:", error);
      setFetchError(`Error fetching journeys: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to directly add a single journey (for testing)
  const addSingleJourney = async () => {
    if (!user) {
      setFetchError("You must be signed in to import data");
      return;
    }

    setLoading(true);
    setFetchError(null);

    try {
      // Enable network temporarily for this operation
      await enableNetwork(db);
      
      console.log("Adding single test journey...");
      
      // Use the first journey from our data
      const journey = supabaseJourneys[0];
      
      // Prepare journey data
      const journeyData = {
        title: journey.title,
        date: journey.date,
        content: journey.content,
        coordinates: journey.coordinates,
        zoom: journey.zoom.toString(),
        image_url: journey.image_url
      };
      
      // Add as a new document (not using the original ID for this test)
      const journeysRef = collection(db, "journeys");
      const docRef = await addDoc(journeysRef, journeyData);
      
      console.log("Test journey added with ID:", docRef.id);
      setFetchError("Test journey added successfully. Try fetching all journeys now.");
      
      // Disable network again after operation completes
      await disableNetwork(db);
    } catch (error) {
      console.error("Error adding test journey:", error);
      setFetchError(`Error adding test journey: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to import Supabase journeys
  const importSupabaseJourneys = async () => {
    if (!user) {
      setFetchError("You must be signed in to import data");
      return;
    }

    setLoading(true);
    setFetchError(null);
    setImportSuccess(false);

    try {
      // Enable network temporarily for this operation
      await enableNetwork(db);
      
      console.log("Starting import of Supabase journeys...");
      
      // First, try with just one journey
      const firstJourney = supabaseJourneys[0];
      
      // Prepare journey data
      const journeyData = {
        title: firstJourney.title,
        date: firstJourney.date,
        content: firstJourney.content,
        coordinates: firstJourney.coordinates,
        zoom: firstJourney.zoom.toString(),
        image_url: firstJourney.image_url
      };
      
      // Add with original ID
      await setDoc(doc(db, "journeys", firstJourney.id), journeyData);
      console.log("First journey imported successfully");
      
      // Now import the rest one by one
      for (let i = 1; i < supabaseJourneys.length; i++) {
        const journey = supabaseJourneys[i];
        
        // Prepare journey data
        const data = {
          title: journey.title,
          date: journey.date,
          content: journey.content,
          coordinates: journey.coordinates,
          zoom: journey.zoom.toString(),
          image_url: journey.image_url
        };
        
        // Add with original ID
        await setDoc(doc(db, "journeys", journey.id), data);
        console.log(`Journey ${i+1}/${supabaseJourneys.length} imported`);
      }
      
      console.log("Import completed successfully!");
      setImportSuccess(true);
      
      // Fetch the imported journeys to display them
      await fetchJourneys();
      
      // Disable network again after operation completes
      await disableNetwork(db);
    } catch (error) {
      console.error("Error importing journeys:", error);
      setFetchError(`Error importing journeys: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // View all journeys on map
  const viewAllJourneysOnMap = () => {
    setSelectedJourney(null);
    setShowingMap(true);
  };

  // View single journey on map
  const viewJourneyOnMap = (journey: Journey) => {
    setSelectedJourney(journey);
    setShowingMap(true);
  };

  // Helper function to display coordinates in a readable format
  const formatCoordinates = (coordinates: string | { longitude: number, latitude: number }): string => {
    if (typeof coordinates === 'string') {
      return coordinates;
    } else {
      return `(${coordinates.longitude},${coordinates.latitude})`;
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">GeoJourney App</h1>
      
      <div className="bg-blue-100 p-4 rounded mb-6">
        <h2 className="text-xl font-bold mb-2">Firebase Authentication</h2>
        
        {!user ? (
          // Authentication form
          <div>
            <div className="mb-4">
              <label className="block mb-1">Email:</label>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="your@email.com"
              />
            </div>
            
            <div className="mb-4">
              <label className="block mb-1">Password:</label>
              <input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Your password"
              />
            </div>
            
            {authError && (
              <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
                {authError}
              </div>
            )}
            
            <div className="flex space-x-2">
              <button 
                onClick={handleSignIn} 
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
              >
                Sign In
              </button>
              
              <button 
                onClick={handleSignUp} 
                disabled={loading}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-green-300"
              >
                Sign Up
              </button>
            </div>
          </div>
        ) : (
          // User info and sign out
          <div>
            <p className="mb-2">Signed in as: <strong>{user.email}</strong></p>
            <button 
              onClick={handleSignOut}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
      
      {user && (
        <div className="bg-green-100 p-4 rounded mb-6">
          <h2 className="text-xl font-bold mb-2">Journeys</h2>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <button 
              onClick={importSupabaseJourneys}
              disabled={loading}
              className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:bg-indigo-300"
            >
              Import Supabase Journeys
            </button>
            
            <button 
              onClick={addSingleJourney}
              disabled={loading}
              className="px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600 disabled:bg-pink-300"
            >
              Add Test Journey
            </button>
            
            <button 
              onClick={fetchJourney}
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
            >
              Fetch Specific Journey
            </button>
            
            <button 
              onClick={fetchJourneys}
              disabled={loading}
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-purple-300"
            >
              Fetch All Journeys
            </button>
            
            {journeys.length > 0 && (
              <button 
                onClick={viewAllJourneysOnMap}
                disabled={loading}
                className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 disabled:bg-teal-300"
              >
                View All on Map
              </button>
            )}
          </div>
          
          {importSuccess && (
            <div className="mb-4 p-2 bg-green-100 text-green-700 rounded border border-green-300">
              Successfully imported all journeys from Supabase! You can now view them on the map.
            </div>
          )}
          
          {fetchError && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
              {fetchError}
            </div>
          )}
          
          {loading && (
            <div className="mb-4 p-2 bg-yellow-100 text-yellow-700 rounded">
              Loading data...
            </div>
          )}
          
          {/* Map View Section */}
          {showingMap && journeys.length > 0 && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-lg">
                  {selectedJourney ? `Map: ${selectedJourney.title}` : 'All Journeys'}
                </h3>
                <button 
                  onClick={() => {
                    setSelectedJourney(null);
                    setShowingMap(false);
                  }}
                  className="px-2 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm"
                >
                  Close Map
                </button>
              </div>
              <MapView journeys={selectedJourney ? [selectedJourney] : journeys} />
            </div>
          )}
          
          {journeys.length > 0 ? (
            <div>
              <h3 className="font-bold mb-2">Chronological Journey ({journeys.length} points):</h3>
              <div className="space-y-3">
                {journeys.map(journey => (
                  <div key={journey.id} className="bg-white p-4 rounded shadow border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-lg">{journey.title || 'Untitled'}</h4>
                        <p className="text-sm text-gray-600">Date: {journey.date}</p>
                        <p className="mt-2 text-gray-700">{journey.content}</p>
                      </div>
                      <button
                        onClick={() => viewJourneyOnMap(journey)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                      >
                        View on Map
                      </button>
                    </div>
                    
                    {/* Journey Image */}
                    {journey.image_url && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500">Image path: {journey.image_url}</p>
                      </div>
                    )}
                    
                    {/* Journey Properties */}
                    <div className="mt-2 text-xs text-gray-500">
                      <p>Coordinates: {formatCoordinates(journey.coordinates)}</p>
                      <p>Zoom: {journey.zoom}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            fetchError ? null : (
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200 text-yellow-800">
                No journeys found. Try importing Supabase journeys or creating a sample journey first.
              </div>
            )
          )}
        </div>
      )}
      
      <div className="bg-gray-100 p-4 rounded">
        <h2 className="font-bold mb-2">About GeoJourney</h2>
        <p className="mb-2">GeoJourney lets you track and visualize your travel experiences on a map.</p>
        <ul className="list-disc ml-5">
          <li>Create journeys for places you've visited</li>
          <li>View your journeys on an interactive map</li>
          <li>Add details like dates, descriptions, and photos</li>
          <li>All data is securely stored with Firebase authentication</li>
        </ul>
      </div>
    </div>
  );
}

// Add this declaration to handle window.L
declare global {
  interface Window {
    L: any;
  }
}

export default App;
